import { CheckCircleOutlined, DisconnectOutlined, LinkOutlined, WarningOutlined } from '@ant-design/icons'
import { Alert, Button, Descriptions, Modal, Spin, Tag } from 'antd'
import { StripeConnectStatus, stripeConnectApi } from 'common/api/StripeConnectApi'
import { FormCard } from 'common/components/FormCard'
import { useAppContext } from 'common/context/AppContext'
import { showError, showMessage } from 'common/util/Util'
import React, { useCallback, useEffect, useState } from 'react'

export const StripeConnectSettings: React.FC = () => {
  const appContext = useAppContext()
  const [status, setStatus] = useState<StripeConnectStatus>()
  const [loading, setLoading] = useState(false)

  const loadStatus = useCallback(() => {
    setLoading(true)
    stripeConnectApi.status()
      .then(setStatus)
      .catch((error) => showError(appContext, error))
      .finally(() => setLoading(false))
  }, [appContext])

  useEffect(loadStatus, [loadStatus])

  const connect = () => {
    const connectWindow = window.open('', '_blank')
    if (!connectWindow) {
      showMessage(appContext.messageApi, 'Error', 'Allow popups to connect your Stripe account.')
      return
    }

    setLoading(true)
    stripeConnectApi.authorizationUrl()
      .then((url) => {
        connectWindow.location.href = url
        const interval = window.setInterval(() => {
          if (connectWindow.closed) {
            window.clearInterval(interval)
            loadStatus()
          }
        }, 1000)
      })
      .catch((error) => {
        connectWindow.close()
        showError(appContext, error)
      })
      .finally(() => setLoading(false))
  }

  const disconnect = () => {
    Modal.confirm({
      title: 'Disconnect Stripe?',
      content: 'Advertisers will no longer be able to add funds through Stripe.',
      okText: 'Disconnect',
      okButtonProps: { danger: true },
      onOk: () => stripeConnectApi.disconnect()
        .then(() => {
          showMessage(appContext.messageApi, 'Success', 'Stripe account disconnected')
          loadStatus()
        })
        .catch((error) => showError(appContext, error)),
    })
  }

  if (loading && !status) return <Spin />

  return (
    <FormCard title="Stripe Connect">
      {!status?.configured && (
        <Alert type="warning" showIcon message="Stripe Connect is not configured by the system owner." />
      )}

      {status?.paymentBlocked && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }}
               message="Payments are blocked"
               description="A refund or dispute requires review by the system owner." />
      )}

      {status?.configured && !status.connected && (
        <Alert
          type="info"
          showIcon
          message="Connect your Stripe account"
          description="Advertiser payments will be processed directly by your Stripe account. System does not store your Stripe secret key."
          action={<Button type="primary" icon={<LinkOutlined />} onClick={connect} loading={loading}>Connect Stripe</Button>}
        />
      )}

      {status?.connected && (
        <>
          <Descriptions bordered column={1} style={{ maxWidth: 720 }}>
            <Descriptions.Item label="Account">{status.accountDisplay}</Descriptions.Item>
            <Descriptions.Item label="Mode">
              <Tag color={status.livemode ? 'green' : 'orange'}>{status.livemode ? 'LIVE' : 'TEST'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Advertiser payments">
              {status.chargesEnabled
                ? <Tag color="success" icon={<CheckCircleOutlined />}>Enabled</Tag>
                : <Tag color="warning" icon={<WarningOutlined />}>Action required in Stripe</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Bank payouts">
              {status.payoutsEnabled
                ? <Tag color="success" icon={<CheckCircleOutlined />}>Enabled</Tag>
                : <Tag color="warning" icon={<WarningOutlined />}>Action required in Stripe</Tag>}
            </Descriptions.Item>
          </Descriptions>
          <Button danger icon={<DisconnectOutlined />} onClick={disconnect} loading={loading} style={{ marginTop: 20 }}>
            Disconnect Stripe
          </Button>
        </>
      )}
    </FormCard>
  )
}
